import { PageContainer } from '@ant-design/pro-components';
import { Button, Card, List, Tag, Typography, Space, message, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getNotifications,
  markNotificationAsRead,
  deleteReadNotifications,
} from '@/services/ant-design-pro/api';
import { formatDateDefault } from '@/utils/dateUtils';
import { useRequest } from 'ahooks';

const { Text } = Typography;

// 自定义事件名称
export const NOTIFICATION_READ_EVENT = 'notification-read';

const NotificationList: React.FC = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<API.Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchNotifications = async (current = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await getNotifications({
        current,
        pageSize,
      });
      if (response.success) {
        setNotifications(response.data);
        setPagination({
          current,
          pageSize,
          total: response.total,
        });
      }
    } catch (_error) {
      message.error(t('pages.notifications.getListFailed'));
    }
    setLoading(false);
  };

  const handleActionClick = async (item: API.Notification) => {
    if (!item.read) {
      try {
        const response = await markNotificationAsRead(item.id);
        if (response.success) {
          fetchNotifications(pagination.current, pagination.pageSize);
          const event = new CustomEvent(NOTIFICATION_READ_EVENT);
          window.dispatchEvent(event);
        }
      } catch (_error) {
        // 标记已读失败不影响跳转
      }
    }
    window.open(item.action.link, '_blank');
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      const response = await markNotificationAsRead(id);
      if (response.success) {
        message.success(t('pages.notifications.markedAsRead'));
        fetchNotifications(pagination.current, pagination.pageSize); // 重新获取列表

        // 发送自定义事件，通知其他组件更新
        const event = new CustomEvent(NOTIFICATION_READ_EVENT);
        window.dispatchEvent(event);
      }
    } catch (_error) {
      message.error(t('pages.notifications.markReadFailed'));
    }
  };

  const handleDeleteRead = () => {
    Modal.confirm({
      title: t('pages.notifications.confirmDeleteTitle'),
      content: t('pages.notifications.confirmDeleteContent'),
      okText: t('pages.notifications.deleteReadMessages'),
      cancelText: t('pages.notifications.cancel'),
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await deleteReadNotifications();
          if (response.success) {
            message.success(t('pages.notifications.deleteSuccess'));
            fetchNotifications(1, pagination.pageSize); // 重新获取列表，回到第一页

            // 发送自定义事件，通知其他组件更新通知计数
            const event = new CustomEvent(NOTIFICATION_READ_EVENT);
            window.dispatchEvent(event);
          }
        } catch (_error) {
          message.error(t('pages.notifications.deleteFailed'));
        }
      },
    });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    fetchNotifications(page, pageSize);
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  useRequest(fetchNotifications, {
    manual: true,
    refreshOnWindowFocus: true,
  });

  return (
    <PageContainer>
      <Card
        extra={
          <Button variant="text" color="danger" onClick={handleDeleteRead}>
            {t('pages.notifications.deleteRead')}
          </Button>
        }
      >
        <List
          itemLayout="vertical"
          dataSource={notifications}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: handlePageChange,
            showSizeChanger: true,
            showQuickJumper: false,
            showTotal: (total) => t('pages.notifications.total', { total }),
          }}
          renderItem={(item) => (
            <List.Item
              extra={
                <Space>
                  {['文档评审', '简易变更', 'CHANGE_REQUEST', '问题处理'].includes(item.type) ? (
                    <Button type="link" onClick={() => handleActionClick(item)}>
                      {item.action.text}
                    </Button>
                  ) : null}
                  {!item.read && (
                    <Button type="link" onClick={() => handleMarkAsRead(item.id)}>
                      {t('pages.notifications.markAsRead')}
                    </Button>
                  )}
                </Space>
              }
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{item.title}</Text>
                    <Tag color={item.read ? 'default' : 'blue'}>
                      {item.read ? t('pages.notifications.read') : t('pages.notifications.unread')}
                    </Tag>
                  </Space>
                }
                description={formatDateDefault(item.time)}
              />
              {item.content}
            </List.Item>
          )}
        />
      </Card>
    </PageContainer>
  );
};

export default NotificationList;
